import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import {
  fetchReviewsByTeam,
  fetchReviewById,
  fetchReviewDecisions,
  fetchProfilesByIds,
  fetchReviewConflicts,
  fetchSourcesByIds,
  fetchDecisionsByReviewAndSources,
  fetchReviewMode,
  buildReviewSourcesQuery,
  buildDecisionsSummaryQuery,
  buildConflictCountQuery,
  buildResolvedConflictCountQuery,
  fetchHumanDecisionsWithProfiles,
  fetchReviewSourcesWithDetails,
  fetchReviewerIdsForKappa,
  rpcComputeCohensKappa,
  insertReview,
  updateReview,
  deleteReview,
  upsertDecision,
  rpcDetectConflicts,
  resolveConflict,
  upsertReviewSources,
  rpcAdvanceToFullText,
} from '@/lib/queries/reviews';
import { buildReviewStats } from '@/features/review/review-stats';
import { useAuthStore } from '@/store/authStore';
import type {
  Review,
  ReviewDecision,
  ReviewConflict,
  ReviewStats,
  ReviewerStats,
  ReviewSource,
  CreateReviewPayload,
  SubmitDecisionPayload,
  ResolveConflictPayload,
  ReviewPhase,
  ReviewStatus,
} from '@/features/review/types';

const REVIEWS_KEY = 'reviews';
const REVIEW_KEY = 'review';
const DECISIONS_KEY = 'review-decisions';
const CONFLICTS_KEY = 'review-conflicts';
const REVIEW_STATS_KEY = 'review-stats';
const REVIEW_SOURCES_KEY = 'review-sources';
const KAPPA_KEY = 'kappa-metrics';

/** Fetch all reviews for a team */
export function useReviews(teamId: string | undefined) {
  return useQuery({
    queryKey: [REVIEWS_KEY, teamId],
    queryFn: async (): Promise<Review[]> => {
      if (!teamId) return [];

      const { data, error } = await fetchReviewsByTeam(teamId);

      if (error) throw error;
      return (data ?? []) as Review[];
    },
    enabled: !!teamId,
  });
}

/** Fetch a single review by ID */
export function useReview(reviewId: string | undefined) {
  return useQuery({
    queryKey: [REVIEW_KEY, reviewId],
    queryFn: async (): Promise<Review | null> => {
      if (!reviewId) return null;

      const { data, error } = await fetchReviewById(reviewId);

      if (error) throw error;
      return data as Review;
    },
    enabled: !!reviewId,
  });
}

/** Fetch decisions for a review, optionally filtered by phase */
export function useReviewDecisions(
  reviewId: string | undefined,
  phase?: string,
) {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: [DECISIONS_KEY, reviewId, phase, user?.id],
    queryFn: async (): Promise<ReviewDecision[]> => {
      if (!reviewId) return [];

      const { data, error } = await fetchReviewDecisions(reviewId, phase);

      if (error) throw error;
      if (!data || data.length === 0) return [];

      // Batch fetch all reviewer profiles in one query
      const reviewerIds = [...new Set(data.map((d) => d.reviewer_id))];
      const { data: profiles } = await fetchProfilesByIds(reviewerIds);

      const profileMap = new Map(
        (profiles ?? []).map((p) => [p.id, { full_name: p.full_name, email: p.email }]),
      );

      const decisions: ReviewDecision[] = data.map((d) => ({
        ...d,
        reviewer: profileMap.get(d.reviewer_id) ?? undefined,
      })) as ReviewDecision[];

      return decisions;
    },
    enabled: !!reviewId,
  });
}

/** Fetch conflicts for a review */
export function useReviewConflicts(reviewId: string | undefined) {
  return useQuery({
    queryKey: [CONFLICTS_KEY, reviewId],
    queryFn: async (): Promise<ReviewConflict[]> => {
      if (!reviewId) return [];

      const { data, error } = await fetchReviewConflicts(reviewId);

      if (error) throw error;
      if (!data || data.length === 0) return [];

      // Collect all unique source IDs and batch fetch
      const sourceIds = [...new Set(data.map((c) => c.source_id))];
      const { data: sources } = await fetchSourcesByIds(sourceIds);

      const sourceMap = new Map(
        (sources ?? []).map((s) => [s.id, { file_name: s.file_name, title: s.title }]),
      );

      // Batch fetch all decisions for this review that match conflict sources/phases
      const { data: allDecisions } = await fetchDecisionsByReviewAndSources(
        reviewId,
        sourceIds,
      );

      // Collect all unique reviewer IDs and batch fetch profiles
      const reviewerIds = [
        ...new Set((allDecisions ?? []).map((d) => d.reviewer_id)),
      ];
      const { data: profiles } = reviewerIds.length > 0
        ? await fetchProfilesByIds(reviewerIds)
        : { data: [] };

      const profileMap = new Map(
        (profiles ?? []).map((p) => [p.id, { full_name: p.full_name, email: p.email }]),
      );

      // Assemble conflicts from in-memory maps
      const conflicts: ReviewConflict[] = data.map((c) => {
        const matchingDecisions = (allDecisions ?? [])
          .filter((d) => d.source_id === c.source_id && d.phase === c.phase)
          .map((d) => ({
            ...d,
            reviewer: profileMap.get(d.reviewer_id) ?? undefined,
          })) as ReviewDecision[];

        return {
          ...c,
          source: sourceMap.get(c.source_id) ?? undefined,
          decisions: matchingDecisions,
        } as ReviewConflict;
      });

      return conflicts;
    },
    enabled: !!reviewId,
  });
}

/** Compute review stats (totals, screened, conflicts, etc.) */
export function useReviewStats(
  reviewId: string | undefined,
  phase?: ReviewPhase,
) {
  return useQuery({
    queryKey: [REVIEW_STATS_KEY, reviewId, phase],
    queryFn: async (): Promise<ReviewStats> => {
      const empty: ReviewStats = {
        total_sources: 0,
        screened: 0,
        included: 0,
        excluded: 0,
        maybe: 0,
        conflicts: 0,
        resolved_conflicts: 0,
      };

      if (!reviewId) return empty;

      const [
        { data: review, error: reviewError },
        { data: reviewSources, error: reviewSourcesError },
        { data: decisions, error: decisionsError },
        { count: conflictCount, error: conflictCountError },
        { count: resolvedCount, error: resolvedCountError },
      ] = await Promise.all([
        fetchReviewMode(reviewId),
        buildReviewSourcesQuery(reviewId, phase),
        buildDecisionsSummaryQuery(reviewId, phase),
        buildConflictCountQuery(reviewId, phase),
        buildResolvedConflictCountQuery(reviewId, phase),
      ]);

      if (reviewError) throw reviewError;
      if (reviewSourcesError) throw reviewSourcesError;
      if (decisionsError) throw decisionsError;
      if (conflictCountError) throw conflictCountError;
      if (resolvedCountError) throw resolvedCountError;

      return buildReviewStats({
        reviewSources: (reviewSources ?? []) as Array<{
          source_id: string;
          current_phase?: string | null;
        }>,
        decisions: (decisions ?? []) as Array<{
          source_id: string;
          decision: 'include' | 'exclude' | 'maybe';
          decision_source?: 'ai' | 'human' | 'ai_confirmed' | null;
          phase?: ReviewPhase | null;
        }>,
        phase,
        reviewMode: (review?.review_mode ?? 'ai_human') as
          | 'ai_only'
          | 'ai_human'
          | 'dual_human'
          | 'full',
        conflicts: conflictCount ?? 0,
        resolvedConflicts: resolvedCount ?? 0,
      });
    },
    enabled: !!reviewId,
  });
}

/** Get per-reviewer statistics */
export function useReviewerStats(reviewId: string | undefined) {
  return useQuery({
    queryKey: ['reviewer-stats', reviewId],
    queryFn: async (): Promise<ReviewerStats[]> => {
      if (!reviewId) return [];

      // Single query: fetch all human decisions with profiles joined
      const { data: allDecisions, error } = await fetchHumanDecisionsWithProfiles(reviewId);

      if (error) throw error;
      if (!allDecisions || allDecisions.length === 0) return [];

      // Group decisions by reviewer for counts
      const reviewerMap = new Map<
        string,
        {
          include: number;
          exclude: number;
          maybe: number;
          total: number;
          profile: { full_name: string | null; email: string } | null;
        }
      >();

      // Group decisions by source for agreement computation
      const decisionsBySource = new Map<
        string,
        Array<{ reviewer_id: string; decision: string }>
      >();

      for (const d of allDecisions) {
        // Build reviewer stats
        if (!reviewerMap.has(d.reviewer_id)) {
          const profile = d.profiles as unknown as { full_name: string | null; email: string } | null;
          reviewerMap.set(d.reviewer_id, {
            include: 0,
            exclude: 0,
            maybe: 0,
            total: 0,
            profile,
          });
        }
        const stats = reviewerMap.get(d.reviewer_id)!;
        stats.total++;
        if (d.decision === 'include') stats.include++;
        else if (d.decision === 'exclude') stats.exclude++;
        else if (d.decision === 'maybe') stats.maybe++;

        // Index by source for agreement
        if (!decisionsBySource.has(d.source_id)) {
          decisionsBySource.set(d.source_id, []);
        }
        decisionsBySource.get(d.source_id)!.push({
          reviewer_id: d.reviewer_id,
          decision: d.decision,
        });
      }

      // Compute agreement rates from in-memory data
      const result: ReviewerStats[] = Array.from(reviewerMap.entries()).map(
        ([reviewerId, stats]) => {
          let agreements = 0;
          let totalWithOthers = 0;

          for (const [, sourceDecs] of decisionsBySource) {
            const mine = sourceDecs.find((d) => d.reviewer_id === reviewerId);
            const others = sourceDecs.filter((d) => d.reviewer_id !== reviewerId);
            if (!mine || others.length === 0) continue;

            totalWithOthers++;

            // Majority decision among others
            const counts: Record<string, number> = {};
            for (const o of others) {
              counts[o.decision] = (counts[o.decision] ?? 0) + 1;
            }
            const topDecision = Object.entries(counts).sort(
              ([, a], [, b]) => b - a,
            )[0]?.[0];

            if (topDecision === mine.decision) agreements++;
          }

          const agreementRate =
            totalWithOthers > 0 ? agreements / totalWithOthers : 0;

          return {
            reviewer_id: reviewerId,
            reviewer_name:
              stats.profile?.full_name ?? stats.profile?.email ?? 'Unknown',
            decisions_count: stats.total,
            include_count: stats.include,
            exclude_count: stats.exclude,
            maybe_count: stats.maybe,
            agreement_rate: Math.round(agreementRate * 100) / 100,
          };
        },
      );

      return result;
    },
    enabled: !!reviewId,
  });
}

/** Create a new review */
export function useCreateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateReviewPayload): Promise<Review> => {
      const { data, error } = await insertReview({
        team_id: payload.team_id,
        name: payload.name,
        description: payload.description ?? null,
        blinded: payload.blinded ?? true,
        review_mode: payload.review_mode ?? 'ai_only',
        settings: payload.settings ?? {},
      });

      if (error) throw error;
      return data as Review;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [REVIEWS_KEY, data.team_id],
      });
      toast.success('Review created');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create review');
    },
  });
}

/** Update a review's status or settings */
export function useUpdateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      reviewId: string;
      name?: string;
      description?: string;
      status?: ReviewStatus;
      blinded?: boolean;
      review_mode?: string;
      settings?: Record<string, unknown>;
    }): Promise<Review> => {
      const updates: Record<string, unknown> = {};
      if (vars.name !== undefined) updates.name = vars.name;
      if (vars.description !== undefined)
        updates.description = vars.description;
      if (vars.status !== undefined) updates.status = vars.status;
      if (vars.blinded !== undefined) updates.blinded = vars.blinded;
      if (vars.review_mode !== undefined) updates.review_mode = vars.review_mode;
      if (vars.settings !== undefined) updates.settings = vars.settings;

      const { data, error } = await updateReview(vars.reviewId, updates);

      if (error) throw error;
      return data as Review;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [REVIEWS_KEY, data.team_id],
      });
      queryClient.invalidateQueries({
        queryKey: [REVIEW_KEY, data.id],
      });
      toast.success('Review updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update review');
    },
  });
}

/** Delete a review */
export function useDeleteReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      reviewId: string;
      teamId: string;
    }): Promise<void> => {
      const { error } = await deleteReview(vars.reviewId);

      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: [REVIEWS_KEY, vars.teamId],
      });
      toast.success('Review deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete review');
    },
  });
}

/** Submit a review decision (include/exclude/maybe) */
export function useSubmitDecision() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: async (
      payload: SubmitDecisionPayload,
    ): Promise<ReviewDecision> => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await upsertDecision({
        review_id: payload.review_id,
        source_id: payload.source_id,
        reviewer_id: user.id,
        decision: payload.decision,
        reason: payload.reason ?? null,
        phase: payload.phase,
        decision_source: payload.decision_source ?? 'human',
        exclusion_reason_category: payload.exclusion_reason_category ?? null,
      });

      if (error) throw error;
      return data as ReviewDecision;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: [DECISIONS_KEY, payload.review_id],
      });
      queryClient.invalidateQueries({
        queryKey: [REVIEW_STATS_KEY, payload.review_id],
      });
      toast.success('Decision submitted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to submit decision');
    },
  });
}

/** Detect conflicts for a review (calls the RPC) */
export function useDetectConflicts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      reviewId: string;
      phase: string;
    }): Promise<number> => {
      const { data, error } = await rpcDetectConflicts(
        vars.reviewId,
        vars.phase,
      );

      if (error) throw error;
      return (data as number) ?? 0;
    },
    onSuccess: (count, vars) => {
      queryClient.invalidateQueries({
        queryKey: [CONFLICTS_KEY, vars.reviewId],
      });
      queryClient.invalidateQueries({
        queryKey: [REVIEW_STATS_KEY, vars.reviewId],
      });
      if (count > 0) {
        toast.warning(`${count} conflict${count > 1 ? 's' : ''} detected`);
      } else {
        toast.success('No conflicts found');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to detect conflicts');
    },
  });
}

/** Resolve a conflict with a final decision */
export function useResolveConflict() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: async (payload: ResolveConflictPayload): Promise<void> => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await resolveConflict(
        payload.conflict_id,
        user.id,
        payload.final_decision,
      );

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONFLICTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [REVIEW_STATS_KEY] });
      toast.success('Conflict resolved');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to resolve conflict');
    },
  });
}

// =========================================
// Review Sources (Phase 1)
// =========================================

/** Fetch sources for a review, optionally filtered by phase */
export function useReviewSources(
  reviewId: string | undefined,
  phase?: string,
) {
  return useQuery({
    queryKey: [REVIEW_SOURCES_KEY, reviewId, phase],
    queryFn: async (): Promise<ReviewSource[]> => {
      if (!reviewId) return [];

      const { data, error } = await fetchReviewSourcesWithDetails(
        reviewId,
        phase,
      );

      if (error) throw error;
      return (data ?? []) as unknown as ReviewSource[];
    },
    enabled: !!reviewId,
  });
}

/** Add sources to a review */
export function useAddSourcesToReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      reviewId: string;
      sourceIds: string[];
    }): Promise<number> => {
      const rows = vars.sourceIds.map((sourceId) => ({
        review_id: vars.reviewId,
        source_id: sourceId,
      }));

      const { error } = await upsertReviewSources(rows);

      if (error) throw error;
      return rows.length;
    },
    onSuccess: (count, vars) => {
      queryClient.invalidateQueries({
        queryKey: [REVIEW_SOURCES_KEY, vars.reviewId],
      });
      toast.success(`${count} source${count !== 1 ? 's' : ''} added to review`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add sources');
    },
  });
}

/** Advance sources from screening to full_text phase */
export function useAdvanceToFullText() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reviewId: string): Promise<number> => {
      const { data, error } = await rpcAdvanceToFullText(reviewId);

      if (error) throw error;
      return (data as number) ?? 0;
    },
    onSuccess: (count, reviewId) => {
      queryClient.invalidateQueries({
        queryKey: [REVIEW_SOURCES_KEY, reviewId],
      });
      queryClient.invalidateQueries({
        queryKey: [REVIEW_KEY, reviewId],
      });
      queryClient.invalidateQueries({
        queryKey: [REVIEW_STATS_KEY, reviewId],
      });
      toast.success(
        `${count} source${count !== 1 ? 's' : ''} advanced to full-text review`,
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to advance to full text');
    },
  });
}

// =========================================
// Cohen's Kappa / IRR (Phase 2)
// =========================================

interface KappaResult {
  reviewer1: string;
  reviewer2: string;
  reviewer1_name: string;
  reviewer2_name: string;
  kappa: number | null;
  n: number;
  observed_agreement: number;
  expected_agreement: number;
  interpretation: string;
}

/** Compute Kappa metrics for all reviewer pairs */
export function useKappaMetrics(
  reviewId: string | undefined,
  phase?: string,
) {
  return useQuery({
    queryKey: [KAPPA_KEY, reviewId, phase],
    queryFn: async (): Promise<KappaResult[]> => {
      if (!reviewId) return [];

      // Get unique reviewers for this review
      const { data: decisions, error: decError } = await fetchReviewerIdsForKappa(
        reviewId,
        phase ?? 'screening',
      );

      if (decError) throw decError;

      const reviewerIds = [...new Set((decisions ?? []).map((d) => d.reviewer_id))];
      if (reviewerIds.length < 2) return [];

      // Fetch reviewer profiles
      const { data: profiles } = await fetchProfilesByIds(reviewerIds);

      const profileMap = new Map(
        (profiles ?? []).map((p) => [p.id, p.full_name ?? p.email]),
      );

      // Compute kappa for each pair
      const results: KappaResult[] = [];
      for (let i = 0; i < reviewerIds.length; i++) {
        for (let j = i + 1; j < reviewerIds.length; j++) {
          const { data, error } = await rpcComputeCohensKappa(
            reviewId,
            reviewerIds[i],
            reviewerIds[j],
            phase ?? 'screening',
          );

          if (error) throw error;
          const kappaData = data as {
            kappa: number | null;
            n: number;
            observed_agreement: number;
            expected_agreement: number;
            interpretation: string;
          };

          results.push({
            reviewer1: reviewerIds[i],
            reviewer2: reviewerIds[j],
            reviewer1_name: profileMap.get(reviewerIds[i]) ?? 'Unknown',
            reviewer2_name: profileMap.get(reviewerIds[j]) ?? 'Unknown',
            ...kappaData,
          });
        }
      }

      return results;
    },
    enabled: !!reviewId,
  });
}

/** Update screening priority for sources */
export function useUpdateScreeningPriority() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reviewId: string): Promise<void> => {
      await api.predictPriority({ review_id: reviewId, phase: 'screening' });
    },
    onSuccess: (_, reviewId) => {
      queryClient.invalidateQueries({
        queryKey: [REVIEW_SOURCES_KEY, reviewId],
      });
      toast.success('Screening priorities updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update priorities');
    },
  });
}
